
import React from "react";
import { Movie } from "@/types/movie";
import MoviePoster from "../MoviePoster";
import MovieTags from "../MovieTags";
import { UnifiedActionButton } from "../buttons";
import { useUnifiedActionButtons } from "@/hooks/useUnifiedActionButtons";
import { getButtonContext } from "@/utils/buttonContextUtils";

interface MovieCardGridProps {
  movie: Movie;
  currentTab?: string;
  isHovered: boolean;
  showAsFavorite: boolean;
  showAsPlanToWatch: boolean;
  handleOpenDialog: () => void;
  handleToggleFavorite: (e: React.MouseEvent) => void;
  handleTogglePlanToWatch: (e: React.MouseEvent) => void;
  setIsHovered: (value: boolean) => void;
}

const MovieCardGrid: React.FC<MovieCardGridProps> = ({
  movie,
  currentTab,
  isHovered,
  showAsFavorite,
  showAsPlanToWatch,
  handleOpenDialog,
  handleToggleFavorite,
  handleTogglePlanToWatch,
  setIsHovered
}) => {
  const { tags, tmdbId } = movie;
  
  // Create movie with effective states for the hook
  const effectiveMovie = {
    ...movie,
    favorite: showAsFavorite,
    planToWatch: showAsPlanToWatch
  };
  
  const buttonContext = getButtonContext("grid");
  
  // FIXED: Remove handler overrides - let useUnifiedActionButtons handle clicks internally
  const { getFavoriteButtonProps, getWatchlistButtonProps } = useUnifiedActionButtons({
    movie: effectiveMovie,
    context: buttonContext,
    currentTab
    // No longer passing onToggleFavorite or onTogglePlanToWatch overrides
  });
  
  const favoriteProps = getFavoriteButtonProps();
  const watchlistProps = getWatchlistButtonProps();
  
  return (
    <div className="flex flex-col">
      <div 
        className="relative cursor-pointer overflow-visible"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-movie-id={tmdbId}
      >
        {/* FIXED: Only the poster is clickable for navigation */}
        <div onClick={handleOpenDialog}>
          <MoviePoster 
            movie={movie} 
            isHovered={isHovered}
          />
        </div>

        {/* FIXED: Action buttons persist without transition - removed opacity transition */}
        <div className={`
          absolute bottom-6 left-0 right-0 px-3 flex flex-row items-center justify-center gap-2
          z-30
          ${isHovered ? 'opacity-100' : 'opacity-0'}
        `}>
          <UnifiedActionButton
            intent="favorite"
            context={buttonContext}
            isActive={favoriteProps.isActive}
            isHovered={favoriteProps.isHovered}
            onClick={favoriteProps.onClick}
            onMouseEnter={favoriteProps.onMouseEnter}
            onMouseLeave={favoriteProps.onMouseLeave}
            showLabel={true}
          />
          
          <UnifiedActionButton
            intent="watchlist"
            context={buttonContext}
            isActive={watchlistProps.isActive}
            isHovered={watchlistProps.isHovered}
            onClick={watchlistProps.onClick}
            onMouseEnter={watchlistProps.onMouseEnter}
            onMouseLeave={watchlistProps.onMouseLeave}
            showLabel={true}
          />
        </div>
      </div>

      <div className="mt-2">
        <MovieTags tags={tags || []} className="mt-1" maxTags={2} />
      </div>
    </div>
  );
};

export default MovieCardGrid;
